import { HStack, KdsText, VStack } from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { useFormContext } from "react-hook-form";
import { ControlledCombobox, ControlledTextField } from "@/components/Form";
import { HighlightIA } from "@/components/OpenBrief/HighlightIA/HighlightIA";
import { type BriefSummaryFormsProps } from "../BriefSummary.types";
import IAEditableField from "./IAEditableField";

export const BriefSummaryContextForm = ({
  initialAIValues,
  productAttributes,
  referentialProducts,
}: BriefSummaryFormsProps) => {
  const { t } = useTranslate();
  const formMethods = useFormContext();

  const productId = formMethods.watch("productId");
  const deliverableId = formMethods.watch("deliverableId");
  const quantity = formMethods.watch("quantity");
  const versionCount = formMethods.watch("versionCount");
  const initialExpression = formMethods.watch("initialExpression");

  const deliverableSelected = productAttributes?.deliverables.find(
    (e) => e.idAdma === deliverableId
  );

  const productOptions =
    referentialProducts
      ?.filter((p) => p.productAdmaId != null)
      .map((p) => ({
        id: String(p.productAdmaId),
        label: t(p.productName),
        name: t(p.productName),
        textValue: `${p.productAdmaId}`,
      })) || [];

  const deliverableOptions =
    productAttributes?.deliverables.map((e) => ({
      id: String(e.idAdma),
      label: t(e.name),
      name: t(e.name),
      textValue: `${e.idAdma}`,
    })) || [];

  const deliverableKey = `deliverable-${productId}-${!!productAttributes}-${deliverableId}`;

  const isInitialAIValuesVersionCountEquals =
    initialAIValues?.versionCount === versionCount;

  return (
    <VStack gap="sp-sm" overflow="hidden">
      <IAEditableField
        value={
          productAttributes ? (
            <HStack gap="sp-xs2" w="fit-content">
              <HighlightIA variant="disabled">
                {t(productAttributes?.productName)}
              </HighlightIA>

              {deliverableSelected && (
                <>
                  <KdsText>/</KdsText>
                  <HighlightIA>{t(deliverableSelected?.name)}</HighlightIA>
                </>
              )}
            </HStack>
          ) : undefined
        }
        label={t("openBrief.summary.productDeliverableTitle")}
      >
        <HStack gap="sp-sm">
          <ControlledCombobox
            name="productId"
            isDisabled={!!initialAIValues?.productId}
            options={productOptions}
          />

          <ControlledCombobox
            key={deliverableKey}
            isDisabled={!productAttributes}
            name="deliverableId"
            options={deliverableOptions}
          />
        </HStack>
      </IAEditableField>

      <IAEditableField
        value={initialExpression}
        label={t("openBrief.summary.expressionOfNeedsTitle")}
      >
        <ControlledTextField name="initialExpression" />
      </IAEditableField>

      <HStack gap="sp-sm">
        <IAEditableField
          IAValue={initialAIValues?.quantity === quantity ? quantity : ""}
          value={initialAIValues?.quantity !== quantity ? quantity : ""}
          label={t("openBrief.summary.quantity")}
        >
          <ControlledTextField type="number" name="quantity" />
        </IAEditableField>

        <IAEditableField
          IAValue={isInitialAIValuesVersionCountEquals ? versionCount : ""}
          value={!isInitialAIValuesVersionCountEquals ? versionCount : ""}
          label={t("openBrief.summary.numberOfVersions")}
        >
          <ControlledTextField type="number" name="versionCount" />
        </IAEditableField>
      </HStack>
    </VStack>
  );
};
